import type { DeploymentLabel, DeploymentTemplateOrigin } from '../core/deploymentLabels'
import { runwayMinutes } from '../core/deposit'
import { hostDisplayName } from '../core/hostName'
import { BLOCK_SECONDS, usdMicrosPerHour } from '../core/pricing'
import type { Coin } from '../core/types'
import { integerRate, sumCoins } from './billing'
import { estimateDepositLeft, normalizedState, parseHeight } from './deposits'
import { HOST_NOT_LISTED, parseDeploymentResources, type InstanceResources } from './instances'
import type { CatalogHostDto, DeploymentDetailDto, LeaseDto } from './types'

/*
  The instance detail page's model (docs/INSTANCE-DETAIL-REDESIGN-PLAN.md §5.2–5.4).

  One place joins the deployment record, its escrow account, its leases, the
  catalog and the labels this browser recorded, so the header, the stepper,
  the banner and the Access badge cannot disagree about what state the
  instance is in. Each source degrades on its own: a missing catalog costs the
  host's name, a missing height turns an estimate into "as of last
  settlement", and nothing unreadable becomes a zero.

  The workload's health is session state — a status read needs a provider
  token — so it is not joined here. The page passes it to `deriveStatus`.
*/

export type InstanceDetailStatus =
  | 'closed'
  | 'out-of-funds'
  | 'needs-signature'
  | 'awaiting-offer'
  | 'attention'
  | 'running'
  | 'starting'
  | 'not-started'
  | 'unchecked'

export type StatusTone = 'positive' | 'info' | 'caution' | 'critical' | 'neutral'

/** What the chain says, independent of anything read from the host this session. */
export interface StatusFacts {
  deploymentClosed: boolean
  outOfFunds: boolean
  hasLease: boolean
}

/** What this page has learned from the host in this session. */
export interface SessionHealth {
  /** Closed by this page a moment ago, before the chain read catches up. */
  closedLocally: boolean
  accountExists: boolean
  /** `leaseHealth(...).health`, or null when no readable status is held. */
  health: 'running' | 'attention' | 'starting' | 'not-reporting' | 'reported' | null
  /** undefined: never read. null: the host has not set the workload up (a 404). */
  statusPayload: unknown
  /** A start is in flight. */
  starting: boolean
  /** This page sent the workload this session. */
  startedThisSession: boolean
}

/**
 * The one status every label on the page reads, first match wins (§5.2).
 *
 * `unchecked` is the honest answer before any status read: after a reload, or
 * for an instance started from another tab, the page does not know whether
 * the workload runs, and used to say it did not.
 */
export function deriveStatus(facts: StatusFacts, session: SessionHealth): InstanceDetailStatus {
  if (session.closedLocally) return 'closed'
  // Before `deploymentClosed`: running out closes the deployment too (see
  // `closeReason`), so checking closed first called every drained instance
  // "closed" and told its tenant the unused deposit went back to them.
  if (facts.outOfFunds) return 'out-of-funds'
  if (facts.deploymentClosed) return 'closed'
  if (!session.accountExists) return 'needs-signature'
  if (!facts.hasLease) return 'awaiting-offer'
  if (session.health === 'attention' || session.health === 'not-reporting') return 'attention'
  if (session.health === 'running') return 'running'
  if (session.starting) return 'starting'
  if (session.health === 'starting' || session.health === 'reported') return 'starting'
  if (session.statusPayload === null) return session.startedThisSession ? 'starting' : 'not-started'
  // A payload the console cannot read as a lease status is still an answer.
  if (session.statusPayload !== undefined) return 'starting'
  return 'unchecked'
}

export interface StatusPresentation {
  label: string
  tone: StatusTone
}

/**
 * `awaitingHost`: the workload was sent and the host has not set it up yet,
 * which is a more useful thing to say than "Starting".
 */
export function statusPresentation(status: InstanceDetailStatus, replicas: { ready: number; total: number } | null = null, awaitingHost = false): StatusPresentation {
  switch (status) {
    case 'closed': return { label: 'Closed', tone: 'neutral' }
    case 'out-of-funds': return { label: 'Ran out of funds', tone: 'critical' }
    case 'needs-signature': return { label: 'Needs signature', tone: 'caution' }
    case 'awaiting-offer': return { label: 'Awaiting your choice of offer', tone: 'caution' }
    case 'attention': return { label: 'Needs attention', tone: 'caution' }
    case 'running': return { label: replicas ? `Running · ${replicas.ready}/${replicas.total} ready` : 'Running', tone: 'positive' }
    case 'starting': return { label: awaitingHost ? 'Sent — waiting for the host' : 'Starting', tone: 'info' }
    case 'not-started': return { label: 'Reserved · not running', tone: 'caution' }
    case 'unchecked': return { label: 'Status not checked', tone: 'neutral' }
  }
}

export type LifecycleStep = 'deposit' | 'host' | 'start' | 'running'
export type StepState = 'done' | 'current' | 'upcoming' | 'problem'

/** The stepper's four steps for a status, or null where the stepper is not shown. */
export function lifecycleSteps(status: InstanceDetailStatus): Record<LifecycleStep, StepState> | null {
  switch (status) {
    case 'awaiting-offer': return { deposit: 'done', host: 'current', start: 'upcoming', running: 'upcoming' }
    case 'attention': return { deposit: 'done', host: 'done', start: 'done', running: 'problem' }
    case 'starting':
    case 'not-started':
    case 'unchecked': return { deposit: 'done', host: 'done', start: 'current', running: 'upcoming' }
    default: return null
  }
}

// ── Money ───────────────────────────────────────────────────────────────────

export interface DepositLeft {
  coin: Coin
  /** Projected from the last settlement to the current height; rendered "about …". */
  estimated: boolean
}

export interface InstanceMoney {
  /** Per block: the sum of the active leases' prices, else this lease's price. */
  rate: Coin | null
  perHourMicros: bigint | null
  perDayMicros: bigint | null
  /** Everything put into the deposit: what is left plus what has been paid out. */
  depositTotal: Coin | null
  depositLeft: DepositLeft | null
  /** Paid out of the deposit so far, the complement of `depositLeft`. */
  used: Coin | null
  /** Billed since the lease began, from heights; null when either height is unknown. */
  usedSinceLease: Coin | null
  runwayMinutes: number | null
}

function micros(coin: Coin | null | undefined): bigint | null {
  return coin && /^\d+$/.test(coin.amount) ? BigInt(coin.amount) : null
}

// Heights and the deposit estimate are `data/deposits.ts`'s, shared with Billing.
export { parseHeight }

function rateOf(leases: readonly LeaseDto[], fallback: LeaseDto | undefined): Coin | null {
  const active = leases.filter((item) => item.lease?.state === 'active')
  if (active.length > 0) return sumCoins(active.map((item) => integerRate(item.lease?.price)))
  return integerRate(fallback?.lease?.price)
}

export function buildMoney(detail: DeploymentDetailDto | null, lease: LeaseDto | undefined, height: number | null): InstanceMoney {
  const rate = rateOf(detail?.leases ?? [], lease)
  const perBlock = micros(rate)
  const perHourMicros = usdMicrosPerHour(rate, BLOCK_SECONDS)
  const escrow = detail?.escrow ?? null
  const balance = micros(escrow?.balance)
  const transferred = micros(escrow?.transferred)
  const denom = escrow?.balance?.denom
  const sameDenom = Boolean(denom) && escrow?.transferred?.denom === denom
  const depositTotal = balance !== null && transferred !== null && sameDenom ? { denom: denom!, amount: (balance + transferred).toString() } : null

  // escrow/v1 moves funds only when an account settles, so between
  // settlements the balance is stale by rate × (height − settledAt). The
  // estimate is `data/deposits.ts`'s, the same one Billing shows.
  const estimate = estimateDepositLeft(escrow?.balance, rate, escrow?.settledAt, height)
  const depositLeft: DepositLeft | null = estimate.left ? { coin: estimate.left, estimated: estimate.estimated } : null
  const leftMicros = micros(depositLeft?.coin)
  const totalMicros = micros(depositTotal)
  const used = depositTotal && leftMicros !== null && totalMicros !== null
    ? { denom: depositTotal.denom, amount: (totalMicros > leftMicros ? totalMicros - leftMicros : 0n).toString() }
    : null

  const created = leaseCreatedHeight(detail?.leases ?? [], lease)
  const usedSinceLease = perBlock !== null && rate && height !== null && created !== null
    ? { denom: rate.denom, amount: (perBlock * BigInt(Math.max(0, height - created))).toString() }
    : null

  return {
    rate,
    perHourMicros,
    perDayMicros: perHourMicros === null ? null : perHourMicros * 24n,
    depositTotal,
    depositLeft,
    used,
    usedSinceLease,
    runwayMinutes: depositLeft ? runwayMinutes(depositLeft.coin.amount, rate) : null,
  }
}

function leaseCreatedHeight(leases: readonly LeaseDto[], fallback: LeaseDto | undefined): number | null {
  const heights = [...leases, ...(fallback ? [fallback] : [])].map((item) => parseHeight(item.lease?.created_at)).filter((value): value is number => value !== null)
  return heights.length > 0 ? Math.min(...heights) : null
}

// ── Time ────────────────────────────────────────────────────────────────────

/** "under a minute", "12 min", "3 h", "2 days": for "about … ago". */
export function describeSpan(seconds: number): string {
  const minutes = Math.floor(Math.max(0, seconds) / 60)
  if (minutes < 1) return 'under a minute'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours} h`
  return `${Math.floor(hours / 24)} days`
}

/** "about 12 min ago", or "just now" inside the first minute. */
export function ago(seconds: number): string {
  return seconds < 60 ? 'just now' : `about ${describeSpan(seconds)} ago`
}

/** "about 12 min ago" from two heights. */
export function agoFromHeights(height: number, eventHeight: number): string {
  return ago((height - eventHeight) * BLOCK_SECONDS)
}

/**
 * When the deposit runs out, in local time: "00:32", or "Fri 11:32" beyond a
 * day. The runway is floored, so this is never later than the real end.
 */
export function runsOutAt(minutes: number, now: number, locale?: string): string {
  const at = new Date(now + minutes * 60_000)
  const options: Intl.DateTimeFormatOptions = minutes >= 24 * 60
    ? { weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false }
    : { hour: '2-digit', minute: '2-digit', hour12: false }
  return new Intl.DateTimeFormat(locale, options).format(at)
}

// ── Activity ────────────────────────────────────────────────────────────────

export interface ActivityEvent {
  kind: 'lease-closed' | 'lease-started' | 'created' | 'sent' | 'running' | 'problem'
  label: string
  height: number | null
  atMs: number | null
}

/** When an event happened, as the tenant reads it. */
export function activityWhen(event: ActivityEvent, height: number | null, now: number): string {
  if (event.atMs !== null) return ago((now - event.atMs) / 1000)
  if (event.height !== null && height !== null && height >= event.height) return agoFromHeights(height, event.height)
  return event.height !== null ? `at block ${event.height}` : ''
}

/** Newest first. Height events and session events are merged on a common clock where one exists. */
export function sortActivity(events: readonly ActivityEvent[], height: number | null, now: number): ActivityEvent[] {
  const at = (event: ActivityEvent) => event.atMs ?? (event.height !== null && height !== null ? now - (height - event.height) * BLOCK_SECONDS * 1000 : event.height ?? 0)
  return [...events].sort((a, b) => at(b) - at(a))
}

// ── The model ───────────────────────────────────────────────────────────────

export interface InstanceDetailInput {
  dseq: string
  detail: DeploymentDetailDto | null
  /** This deployment's lease, resolved by the route. */
  lease: LeaseDto | undefined
  /** The host holding the lease, when one does. */
  provider: string | undefined
  catalogHosts: readonly CatalogHostDto[] | null
  labels: Record<string, DeploymentLabel>
  /** The chain's current height; null when it could not be read. */
  height: number | null
}

export interface InstanceDetailModel {
  dseq: string
  name: string
  nameSource: 'recorded' | 'fallback'
  image: string | null
  /** The SDL template this browser recorded it was created from, if any: the App tab reads it. */
  template?: DeploymentTemplateOrigin
  /** Null until a host holds the lease; "Host not listed" when the catalog does not name it. */
  hostName: string | null
  deploymentState: string | null
  resources: InstanceResources | null
  facts: StatusFacts
  /** Why it closed, how long it ran and what it paid; read whether or not it is closed. */
  closure: Closure
  money: InstanceMoney
  leaseCreatedHeight: number | null
  height: number | null
  /** Chain events, newest first; the page adds this session's. */
  activity: ActivityEvent[]
}

// ── Why it closed ───────────────────────────────────────────────────────────

/*
  RENT-RUNTIME-PLAN W3, decision D3 as revised on 2026-10-06.

  The chain's `Lease.reason` cannot answer this. `OnLeaseClosed` writes the
  lease's state and `closed_on` and puts the reason only in the
  `EventLeaseClosed` event, so every closed lease reads `lease_closed_invalid`
  (all 28 on the lab chain did). Nor does the lease state say
  `insufficient_funds`: escrow's `save()` runs the account hook before the
  payment hook, the account hook closes the deployment, and closing its groups
  closes the leases as `closed` with reason owner first.

  What the chain does keep is the escrow account's state and the groups'. A
  settlement that runs the funds below zero leaves the account `overdrawn` and
  the groups `insufficient_funds`; a tenant's close leaves both `closed`. On the
  lab chain those two shapes covered all 28 closed leases, 22 and 6, with no
  third. Anything else is "closed" with no reason given.
*/

export type CloseReason = 'ran-out' | 'by-you' | 'unknown'

function groupStates(groups: readonly unknown[] | undefined): string[] {
  return (groups ?? []).map((group) => (group && typeof group === 'object' ? (group as { state?: unknown }).state : undefined)).filter((state): state is string => typeof state === 'string')
}

/** Whether the chain recorded that this deployment's deposit ran out. Evidence only, never inferred from a balance. */
export function ranOutOfFunds(detail: DeploymentDetailDto | null, leases: readonly LeaseDto[]): boolean {
  return normalizedState(detail?.escrow?.state) === 'insufficient-funds'
    || groupStates(detail?.groups).includes('insufficient_funds')
    || leases.some((item) => item.lease?.state === 'insufficient_funds')
}

export function closeReason(detail: DeploymentDetailDto | null, leases: readonly LeaseDto[]): CloseReason {
  if (ranOutOfFunds(detail, leases)) return 'ran-out'
  const deploymentClosed = text(detail?.deployment?.state) === 'closed'
  if (deploymentClosed && normalizedState(detail?.escrow?.state) === 'closed') return 'by-you'
  return 'unknown'
}

export interface Closure {
  reason: CloseReason
  /** The height the lease closed at; null when unreadable. */
  closedHeight: number | null
  /** How long the lease ran, from its heights; null without both. */
  ranMinutes: number | null
  /** Paid to the host in all. After running out, that is the whole deposit. */
  paid: Coin | null
}

function closureOf(detail: DeploymentDetailDto | null, leases: readonly LeaseDto[]): Closure {
  const spans = leases
    .map((item) => ({ from: parseHeight(item.lease?.created_at), to: parseHeight(item.lease?.closed_on) }))
    .filter((span): span is { from: number; to: number } => span.from !== null && span.to !== null && span.to >= span.from)
  const closedHeight = spans.length > 0 ? Math.max(...spans.map((span) => span.to)) : null
  const ranMinutes = spans.length > 0 ? Math.floor((Math.max(...spans.map((span) => span.to - span.from)) * BLOCK_SECONDS) / 60) : null
  const transferred = detail?.escrow?.transferred
  return {
    reason: closeReason(detail, leases),
    closedHeight,
    ranMinutes,
    paid: transferred?.denom && /^\d+$/.test(transferred.amount ?? '') ? transferred : null,
  }
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

export function buildInstanceDetail(input: InstanceDetailInput): InstanceDetailModel {
  const { detail, dseq } = input
  const recorded = Object.prototype.hasOwnProperty.call(input.labels, dseq) ? input.labels[dseq] : undefined
  const catalogHost = input.provider ? input.catalogHosts?.find((host) => host.owner === input.provider) : undefined
  const hostName = input.provider ? (catalogHost ? hostDisplayName(catalogHost) : HOST_NOT_LISTED) : null
  const deploymentState = text(detail?.deployment?.state)
  const leases = detail?.leases ?? (input.lease ? [input.lease] : [])
  const created = leaseCreatedHeight(leases, input.lease)

  const activity: ActivityEvent[] = []
  for (const item of leases) {
    const closedOn = parseHeight(item.lease?.closed_on)
    if (closedOn !== null) activity.push({ kind: 'lease-closed', label: 'Lease closed', height: closedOn, atMs: null })
  }
  if (created !== null) activity.push({ kind: 'lease-started', label: `Lease started with ${hostName ?? HOST_NOT_LISTED} · billing started`, height: created, atMs: null })
  // The console creates a deployment at the chain height, so its dseq is its
  // creation height — but only for deployments this console created, which
  // is what a recorded label vouches for.
  const dseqHeight = parseHeight(dseq)
  if (recorded && dseqHeight !== null) activity.push({ kind: 'created', label: 'Instance created', height: dseqHeight, atMs: null })

  return {
    dseq,
    name: recorded ? recorded.name : `Deployment #${dseq}`,
    nameSource: recorded ? 'recorded' : 'fallback',
    image: recorded?.image ?? null,
    ...(recorded?.template ? { template: recorded.template } : {}),
    hostName,
    deploymentState,
    resources: detail ? parseDeploymentResources(detail) : null,
    facts: {
      deploymentClosed: deploymentState === 'closed',
      outOfFunds: ranOutOfFunds(detail, leases),
      hasLease: Boolean(input.provider),
    },
    closure: closureOf(detail, leases),
    money: buildMoney(detail, input.lease, input.height),
    leaseCreatedHeight: created,
    height: input.height,
    activity: sortActivity(activity, input.height, 0),
  }
}
